/**
 * Purchasing Email Notifications Section (CR-4421).
 * Lets an admin configure procurement email frequency, recipient departments,
 * and weekly arrears report toggles for customer and supplier sides.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Mail } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

const DEPARTMENT_OPTIONS: { value: string; label: string }[] = [
  { value: 'quality', label: 'Quality Department Users' },
  { value: 'purchasing', label: 'Procurement Department Users' },
  { value: 'engineering', label: 'Engineering Department Users' },
  { value: 'production', label: 'Production Department Users' },
  { value: 'management', label: 'Management Users' },
  { value: 'all', label: 'All Users' },
];

const WEEKDAY_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

type EmailFrequency = 'weekly' | 'weekdays';

export function PurchasingEmailNotificationsSection() {
  const [loading, setLoading] = useState(true);

  const [customerDepartment, setCustomerDepartment] = useState<string>('purchasing');
  const [supplierDepartment, setSupplierDepartment] = useState<string>('purchasing');
  const [emailFrequency, setEmailFrequency] = useState<EmailFrequency>('weekdays');
  const [emailWeekdays, setEmailWeekdays] = useState<number[]>([]);
  const [arrearsSupplier, setArrearsSupplier] = useState<boolean>(false);
  const [arrearsCustomer, setArrearsCustomer] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        setCustomerDepartment(data.purchasing_customer_department ?? 'purchasing');
        setSupplierDepartment(data.purchasing_supplier_department ?? 'purchasing');
        setEmailFrequency((data.purchasing_email_frequency ?? 'weekdays') as EmailFrequency);
        setEmailWeekdays(data.purchasing_email_weekdays ?? []);
        setArrearsSupplier(!!data.purchasing_arrears_report_supplier);
        setArrearsCustomer(!!data.purchasing_arrears_report_customer);
      } catch (err: unknown) {
        console.error('Error loading purchasing email settings:', err);
        toast.error('Failed to load email notification settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCustomerDepartmentChange = async (value: string) => {
    const previous = customerDepartment;
    setCustomerDepartment(value);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_customer_department: value });
      toast.success('Customer department saved');
    } catch (err: unknown) {
      console.error('Error saving customer department:', err);
      toast.error('Failed to save customer department');
      setCustomerDepartment(previous);
    }
  };

  const handleSupplierDepartmentChange = async (value: string) => {
    const previous = supplierDepartment;
    setSupplierDepartment(value);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_supplier_department: value });
      toast.success('Supplier department saved');
    } catch (err: unknown) {
      console.error('Error saving supplier department:', err);
      toast.error('Failed to save supplier department');
      setSupplierDepartment(previous);
    }
  };

  const handleFrequencyChange = async (value: EmailFrequency) => {
    const previous = emailFrequency;
    setEmailFrequency(value);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_email_frequency: value });
      toast.success('Email frequency saved');
    } catch (err: unknown) {
      console.error('Error saving email frequency:', err);
      toast.error('Failed to save email frequency');
      setEmailFrequency(previous);
    }
  };

  const handleWeekdayToggle = async (day: number) => {
    const previous = emailWeekdays;
    const next = previous.includes(day)
      ? previous.filter((d) => d !== day)
      : [...previous, day].sort((a, b) => a - b);
    setEmailWeekdays(next);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_email_weekdays: next });
      toast.success('Weekday selection saved');
    } catch (err: unknown) {
      console.error('Error saving weekday selection:', err);
      toast.error('Failed to save weekday selection');
      setEmailWeekdays(previous);
    }
  };

  const handleArrearsSupplierChange = async (checked: boolean) => {
    const previous = arrearsSupplier;
    setArrearsSupplier(checked);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_arrears_report_supplier: checked });
      toast.success('Supplier arrears report setting saved');
    } catch (err: unknown) {
      console.error('Error saving supplier arrears setting:', err);
      toast.error('Failed to save supplier arrears report setting');
      setArrearsSupplier(previous);
    }
  };

  const handleArrearsCustomerChange = async (checked: boolean) => {
    const previous = arrearsCustomer;
    setArrearsCustomer(checked);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_arrears_report_customer: checked });
      toast.success('Customer arrears report setting saved');
    } catch (err: unknown) {
      console.error('Error saving customer arrears setting:', err);
      toast.error('Failed to save customer arrears report setting');
      setArrearsCustomer(previous);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Notifications
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Configure procurement email frequency, recipients, and weekly arrears reports.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="purchasing-email-frequency" className="text-sm text-muted-foreground">
            Procurement - Email Frequency
          </Label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <select
              id="purchasing-email-frequency"
              value={emailFrequency}
              onChange={(e) => handleFrequencyChange(e.target.value as EmailFrequency)}
              className={`${selectClass} sm:w-[200px]`}
            >
              <option value="weekly">Weekly</option>
              <option value="weekdays">Weekdays</option>
            </select>
            {emailFrequency === 'weekdays' && (
              <div className="flex flex-wrap gap-x-4 gap-y-2 sm:pt-2">
                {WEEKDAY_OPTIONS.map((day) => (
                  <label key={day.value} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={emailWeekdays.includes(day.value)}
                      onChange={() => handleWeekdayToggle(day.value)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    {day.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="purchasing-customer-department" className="text-sm text-muted-foreground">
              Customer
            </Label>
            <select
              id="purchasing-customer-department"
              value={customerDepartment}
              onChange={(e) => handleCustomerDepartmentChange(e.target.value)}
              className={selectClass}
            >
              {DEPARTMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="purchasing-supplier-department" className="text-sm text-muted-foreground">
              Supplier
            </Label>
            <select
              id="purchasing-supplier-department"
              value={supplierDepartment}
              onChange={(e) => handleSupplierDepartmentChange(e.target.value)}
              className={selectClass}
            >
              {DEPARTMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
            <div className="space-y-1">
              <Label htmlFor="arrears-supplier-toggle" className="text-sm font-medium">
                Weekly Arrears Report - Supplier
              </Label>
              <p className="text-xs text-muted-foreground">
                Send a weekly arrears summary to supplier recipients.
              </p>
            </div>
            <Switch
              id="arrears-supplier-toggle"
              checked={arrearsSupplier}
              onCheckedChange={handleArrearsSupplierChange}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
            <div className="space-y-1">
              <Label htmlFor="arrears-customer-toggle" className="text-sm font-medium">
                Weekly Arrears Report - Customer
              </Label>
              <p className="text-xs text-muted-foreground">
                Send a weekly arrears summary to customer recipients.
              </p>
            </div>
            <Switch
              id="arrears-customer-toggle"
              checked={arrearsCustomer}
              onCheckedChange={handleArrearsCustomerChange}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
